import type { CoachSummary } from "../coachSummary";

type CoachSummaryCardProps = {
  summary: CoachSummary;
};

function formatEvidenceValue(value: string | number, unit: string | null): string {
  if (typeof value === "number") {
    return unit ? `${value} ${unit}` : String(value);
  }
  return unit ? `${value} ${unit}` : value;
}

function titleCase(value: string): string {
  return value.split("-").map(segment => segment.charAt(0).toUpperCase() + segment.slice(1)).join(" ");
}

export function CoachSummaryCard({ summary }: CoachSummaryCardProps) {
  return (
    <section className="coach-summary-card" aria-labelledby="coach-summary-title">
      <div className="coach-summary-header">
        <div>
          <p className="eyebrow">COACH SUMMARY</p>
          <h2 id="coach-summary-title">Evidence-grounded session summary</h2>
          <p className="coach-summary-overall">{summary.overallAssessment}</p>
        </div>
        <div className="coach-summary-badges">
          <span className="coach-summary-badge">Evidence-based</span>
          <span className={`coach-summary-badge confidence-${summary.overallConfidence}`}>{titleCase(summary.overallConfidence)} confidence</span>
        </div>
      </div>

      <ol className="coach-observation-list" aria-label="Key observations">
        {summary.observations.map(observation => (
          <li key={observation.id} className="coach-observation-item">
            <div className="coach-observation-heading">
              <div>
                <span className="coach-rank">#{observation.rank}</span>
                <h3>{observation.headline}</h3>
              </div>
              <div className="coach-observation-tags">
                <span>{titleCase(observation.domain)}</span>
                <span>{titleCase(observation.evidenceType)}</span>
                <span className={`confidence-${observation.confidence}`}>{titleCase(observation.confidence)}</span>
              </div>
            </div>
            <p>{observation.explanation}</p>
            <details>
              <summary>Evidence details</summary>
              <ul>
                {observation.evidence.map(item => (
                  <li key={`${observation.id}-${item.metric}`}>
                    <strong>{item.metric}:</strong> {formatEvidenceValue(item.value, item.unit)}
                    <span>{item.source}</span>
                  </li>
                ))}
              </ul>
            </details>
          </li>
        ))}
      </ol>

      <div className="coach-takeaway">
        <h3>Coaching takeaway</h3>
        <p>{summary.coachingTakeaway}</p>
      </div>

      {summary.limitations.length > 0 ? (
        <div className="coach-limitations">
          <h3>Data limitations</h3>
          <ul>
            {summary.limitations.map(item => <li key={item}>{item}</li>)}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
